import type { ReactNode } from "react";
import Image from "next/image";
import { eyebrowClass, themeClasses, type Theme } from "@cw/design-tokens/theme";
import { ContainedSection } from "../primitives/Section";
import { LinkRow, type LinkSpec } from "../primitives/Button";

export interface FeatureSplitProps {
  eyebrow?: string;
  heading: string;
  children?: ReactNode;
  image?: { src: string; alt: string; width: number; height: number };
  visual?: ReactNode;
  imageSide?: "left" | "right";
  fit?: "cover" | "contain";
  theme?: Theme;
  links?: LinkSpec[];
  id?: string;
}

export function FeatureSplit({ eyebrow, heading, children, image, visual, imageSide = "right", fit = "cover", theme = "paper", links, id }: FeatureSplitProps) {
  const t = themeClasses(theme);
  const media = visual ?? (image ? (
    <Image src={image.src} alt={image.alt} width={image.width} height={image.height} sizes="(min-width: 768px) 50vw, 100vw"
      className={fit === "contain" ? "h-auto w-full object-contain" : "aspect-[4/3] w-full rounded-card object-cover shadow-card"} />
  ) : null);
  return (
    <ContainedSection theme={theme} id={id}>
      <div className="grid items-center gap-10 md:grid-cols-2 md:gap-16">
        <div className={imageSide === "left" ? "md:order-2" : ""}>
          {eyebrow ? <p className={`${eyebrowClass(theme)} mb-3 font-sans text-xs font-semibold uppercase tracking-[0.18em]`}>{eyebrow}</p> : null}
          <h2 className={`${t.heading} text-display-md font-semibold`}>{heading}</h2>
          {children ? <div className={`${t.prose} ${t.body} mt-5 text-lg`}>{children}</div> : null}
          <LinkRow links={links} theme={theme} className="mt-8" />
        </div>
        {media ? <div className={imageSide === "left" ? "md:order-1" : ""}>{media}</div> : null}
      </div>
    </ContainedSection>
  );
}
